import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Command, Rocket, Brain, LineChart, Target } from 'lucide-react';
import { useSound } from '../hooks/useSound';

const solutions = [
  {
    icon: Brain,
    title: 'Strategic Business Optimization',
    description: 'AI-driven insights and expert guidance for sustained profitability.',
    features: [
      'Process optimization strategies',
      'Resource allocation improvements',
      'Workflow streamlining',
      'Quality control enhancement'
    ]
  },
  {
    icon: LineChart,
    title: 'Financial Performance',
    description: 'Comprehensive financial strategy and optimization.',
    features: [
      'Strategic growth planning',
      'Cost efficiency optimization',
      'Revenue expansion strategies',
      'Market opportunity identification'
    ]
  },
  {
    icon: Target,
    title: 'AI-Powered Business Intelligence',
    description: 'Real-time insights and strategic recommendations.',
    features: [
      'Daily performance insights',
      'Market trend analysis',
      'Competitive benchmarking',
      'Tactical recommendations'
    ]
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Solutions = () => {
  const { playHover } = useSound();

  return (
    <section className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Solutions We Offer
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We provide AI-driven insights and expert guidance to help businesses achieve sustained profitability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
            >
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.8, ...springConfig }}
                onMouseEnter={playHover}
                className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.8, ...springConfig }}
                  className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-700"
                >
                  <solution.icon className="w-6 h-6 text-amber-400" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-2">{solution.title}</h3>
                <p className="text-gray-400 mb-4">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};